import { useState } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Box,
  Button,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { IOSSwitch } from '../IOSSwitch';

interface RouterAppVisibilityEditProps {
  onSubmit: () => void;
  cpeId: string;
  appVisibilityEnabled: boolean;
}

export const RouterAppVisibilityEdit = ({ onSubmit, cpeId, appVisibilityEnabled }: RouterAppVisibilityEditProps) => {
  const api = useApi();
  const [newAppVisibilityEnabled, setNewAppVisibilityEnabled] = useState<boolean>(appVisibilityEnabled);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(true);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewAppVisibilityEnabled(e.target.checked);
    setDisableSubmit(e.target.checked === appVisibilityEnabled);
  };

  const toggleAppVisibility = useMutation(api.toggleCpeAppVisibilityApiV1ToggleCpeAppVisibilityPost, {
    onSuccess() {
      toast.success(`${newAppVisibilityEnabled ? 'Enabled' : 'Disabled'} app visibility successfully.`);
      onSubmit();
    },
    onError: (error) => {
      apiError(error);
      setDisableSubmit(false);
    },
  });

  const handleSubmit = () => {
    setDisableSubmit(true);
    toggleAppVisibility.mutate({
      cpe_id: cpeId,
      enabled: newAppVisibilityEnabled || false
    });
  };

  return (
    <Box pt={2}>
      <Typography variant="body1" sx={{ mb: 6 }}>
	      Enable or disable app visibility.
      </Typography>
      <Box display="flex" justifyContent="center" sx={{ mb: 4 }}>
        <FormControlLabel
          value="top"
          control={
            <IOSSwitch color="primary" checked={newAppVisibilityEnabled} onChange={handleChange} />
          }
          label={newAppVisibilityEnabled ? 'Enabled' : 'Disabled'}
          labelPlacement="bottom"
          sx={(theme) => ({ '&:hover': { color: theme.palette.primary.main } })}
          componentsProps={{
            typography: {
              sx: (theme) => ({
                fontSize: 12,
                color: !newAppVisibilityEnabled ? theme.palette.grey[600] : theme.palette.primary.main,
              }),
            },
          }}
        />
      </Box>
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="outlined" onClick={handleSubmit} disabled={disableSubmit}>
          Confirm
        </Button>
      </Box>
    </Box>
  );
};
